import type { ConnectSlug } from './channelMeta';

/**
 * Per-channel connect forms: the field models, the client-side validation that
 * gates submission, and the builders for `POST /inboxes` / `PATCH /inboxes/:id
 * {channel}` bodies.
 *
 * Client validation covers only what the server would bounce structurally — the
 * live credential probes stay server-side, and their 1001 messages are shown
 * verbatim.
 */

export const E164 = /^\+[1-9]\d{5,14}$/;

export function isE164(value: string): boolean {
  return E164.test(value.trim());
}

export type FieldErrors = Record<string, string>;

// --------------------------------------------------------------------- models

export interface OauthEmailForm {
  kind: 'oauth-email';
  provider: 'google' | 'microsoft';
  name: string;
  /** Optional preset: consent from any other account is then rejected. */
  email: string;
}

export interface ManualEmailForm {
  kind: 'manual-email';
  name: string;
  email: string;
  imapEnabled: boolean;
  imapAddress: string;
  imapPort: string;
  imapLogin: string;
  imapPassword: string;
  imapSsl: boolean;
  smtpEnabled: boolean;
  smtpAddress: string;
  smtpPort: string;
  smtpLogin: string;
  smtpPassword: string;
  smtpStarttls: boolean;
}

export interface RingcentralForm {
  kind: 'ringcentral';
  name: string;
  phoneNumber: string;
  /** 'oauth' = sign-in flow after create; 'jwt' = paste the credential trio. */
  auth: 'oauth' | 'jwt';
  clientId: string;
  clientSecret: string;
  jwt: string;
}

export interface WhatsappForm {
  kind: 'whatsapp';
  name: string;
  phoneNumber: string;
  phoneNumberId: string;
  businessAccountId: string;
  apiKey: string;
  /** From the tenant's own Meta app; Meta signs every webhook with it. */
  appSecret: string;
}

export interface TelegramForm {
  kind: 'telegram';
  name: string;
  botToken: string;
}

/** The manual Messenger connect: a Page id and a Page token (from the tenant's own Meta app). */
export interface FacebookForm {
  kind: 'facebook';
  name: string;
  pageId: string;
  pageAccessToken: string;
  /** Meta signs the Page's webhooks with the secret of the app the token came from. */
  appSecret: string;
}

/** The manual Instagram connect: a pasted Instagram-login access token. */
export interface InstagramForm {
  kind: 'instagram';
  name: string;
  accessToken: string;
  appSecret: string;
}

export interface TwilioForm {
  kind: 'twilio';
  name: string;
  accountSid: string;
  authToken: string;
  medium: 'sms' | 'whatsapp';
  addressKind: 'phoneNumber' | 'messagingServiceSid';
  phoneNumber: string;
  messagingServiceSid: string;
  voiceEnabled: boolean;
  apiKeySid: string;
  apiKeySecret: string;
}

export interface WebsiteForm {
  kind: 'website';
  name: string;
  widgetColor: string;
  widgetPosition: 'left' | 'right';
  welcomeTitle: string;
  welcomeTagline: string;
  launcherText: string;
  /** One origin per line; empty = any site may embed. */
  allowedOrigins: string;
  /** Blank = the platform Firebase project (identity sync on by default). */
  firebaseProjectId: string;
}

export type ChannelForm =
  | OauthEmailForm
  | ManualEmailForm
  | RingcentralForm
  | WhatsappForm
  | TelegramForm
  | FacebookForm
  | InstagramForm
  | TwilioForm
  | WebsiteForm;

export function emptyForm(slug: ConnectSlug): ChannelForm {
  switch (slug) {
    case 'google':
      return { kind: 'oauth-email', provider: 'google', name: '', email: '' };
    case 'microsoft':
      return { kind: 'oauth-email', provider: 'microsoft', name: '', email: '' };
    case 'imap-smtp':
      return {
        kind: 'manual-email',
        name: '',
        email: '',
        imapEnabled: true,
        imapAddress: '',
        imapPort: '993',
        imapLogin: '',
        imapPassword: '',
        imapSsl: true,
        smtpEnabled: true,
        smtpAddress: '',
        smtpPort: '587',
        smtpLogin: '',
        smtpPassword: '',
        smtpStarttls: true,
      };
    case 'ringcentral':
      return {
        kind: 'ringcentral',
        name: '',
        phoneNumber: '',
        auth: 'oauth',
        clientId: '',
        clientSecret: '',
        jwt: '',
      };
    case 'whatsapp':
      return {
        kind: 'whatsapp',
        name: '',
        phoneNumber: '',
        phoneNumberId: '',
        businessAccountId: '',
        apiKey: '',
        appSecret: '',
      };
    case 'telegram':
      return { kind: 'telegram', name: '', botToken: '' };
    case 'facebook':
      return { kind: 'facebook', name: '', pageId: '', pageAccessToken: '', appSecret: '' };
    case 'instagram':
      return { kind: 'instagram', name: '', accessToken: '', appSecret: '' };
    case 'website':
      return {
        kind: 'website',
        name: '',
        widgetColor: '#0E7C86',
        widgetPosition: 'right',
        welcomeTitle: '',
        welcomeTagline: '',
        launcherText: '',
        allowedOrigins: '',
        firebaseProjectId: '',
      };
    case 'twilio':
      return {
        kind: 'twilio',
        name: '',
        accountSid: '',
        authToken: '',
        medium: 'sms',
        addressKind: 'phoneNumber',
        phoneNumber: '',
        messagingServiceSid: '',
        voiceEnabled: false,
        apiKeySid: '',
        apiKeySecret: '',
      };
  }
}

// ----------------------------------------------------------------- validation

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * Fields the server stores encrypted and only overwrites when non-blank. In
 * patch mode (`secretsOptional`) a blank one means "keep the stored value".
 */
const SECRET_FIELDS = new Set([
  'imapPassword',
  'smtpPassword',
  'clientSecret',
  'jwt',
  'apiKey',
  'appSecret',
  'botToken',
  'authToken',
  'apiKeySecret',
  'pageAccessToken',
  'accessToken',
]);

export function validateForm(
  form: ChannelForm,
  { secretsOptional = false }: { secretsOptional?: boolean } = {},
): FieldErrors {
  const errors: FieldErrors = {};
  const need = (field: string, value: string, label: string) => {
    if (secretsOptional && SECRET_FIELDS.has(field)) return;
    if (value.trim() === '') errors[field] = `${label} is required.`;
  };

  if (form.name.trim() === '') errors['name'] = 'Give the inbox a name.';

  switch (form.kind) {
    case 'oauth-email':
      if (form.email.trim() !== '' && !EMAIL.test(form.email.trim())) {
        errors['email'] = 'That does not look like an email address.';
      }
      break;

    case 'manual-email': {
      need('email', form.email, 'The mailbox address');
      if (form.email.trim() !== '' && !EMAIL.test(form.email.trim())) {
        errors['email'] = 'That does not look like an email address.';
      }
      if (!form.imapEnabled && !form.smtpEnabled) {
        errors['imapEnabled'] = 'Enable IMAP (receiving), SMTP (sending), or both.';
      }
      if (form.imapEnabled) {
        need('imapAddress', form.imapAddress, 'The IMAP server');
        need('imapLogin', form.imapLogin, 'The IMAP login');
        need('imapPassword', form.imapPassword, 'The IMAP password');
      }
      if (form.smtpEnabled) {
        need('smtpAddress', form.smtpAddress, 'The SMTP server');
        need('smtpLogin', form.smtpLogin, 'The SMTP login');
        need('smtpPassword', form.smtpPassword, 'The SMTP password');
      }
      break;
    }

    case 'ringcentral':
      if (!isE164(form.phoneNumber)) {
        errors['phoneNumber'] = 'Include the country code, e.g. +15166132700.';
      }
      if (form.auth === 'jwt') {
        need('clientId', form.clientId, 'The client ID');
        need('clientSecret', form.clientSecret, 'The client secret');
        need('jwt', form.jwt, 'The JWT credential');
      }
      break;

    case 'whatsapp':
      if (!isE164(form.phoneNumber)) {
        errors['phoneNumber'] = 'The number must be E.164, e.g. +15551234567.';
      }
      need('phoneNumberId', form.phoneNumberId, 'The phone number ID');
      need('apiKey', form.apiKey, 'The system-user token');
      // The webhook override is written to the WABA and templates sync from it, so
      // without the id the channel connects and then neither receives nor sends. The
      // app secret is deliberately not required here: only the server knows whether
      // this deployment has a platform Meta app to fall back on, and it rejects the
      // connect with that reason if not.
      need('businessAccountId', form.businessAccountId, 'The business account ID');
      break;

    case 'telegram':
      need('botToken', form.botToken, 'The bot token');
      break;

    case 'facebook':
      if (!/^\d{5,}$/.test(form.pageId.trim())) errors['pageId'] = 'The Page ID is a number.';
      need('pageAccessToken', form.pageAccessToken, 'The Page access token');
      // The app secret is deliberately not required here: only the server knows
      // whether this deployment has a platform Meta app to fall back on.
      break;

    case 'instagram':
      need('accessToken', form.accessToken, 'The access token');
      break;

    case 'website': {
      if (!/^#[0-9a-fA-F]{6}$/.test(form.widgetColor.trim())) {
        errors['widgetColor'] = 'Use a hex color like #0E7C86.';
      }
      for (const line of splitOrigins(form.allowedOrigins)) {
        if (!isBareOrigin(line)) {
          errors['allowedOrigins'] = `"${line}" is not a bare origin like https://www.example.com.`;
          break;
        }
      }
      break;
    }

    case 'twilio': {
      need('accountSid', form.accountSid, 'The account SID');
      need('authToken', form.authToken, 'The auth token');
      // The server wants exactly one of the two addresses.
      if (form.addressKind === 'phoneNumber') {
        // The country code is mandatory: the server no longer guesses +1 for the
        // channel's own number, so a bare ten-digit one is refused, not adopted.
        if (!isE164(form.phoneNumber)) {
          errors['phoneNumber'] = 'Include the country code, e.g. +15551234567.';
        }
      } else {
        need('messagingServiceSid', form.messagingServiceSid, 'The messaging service SID');
      }
      // WhatsApp senders address by number, never by service SID.
      if (form.medium === 'whatsapp' && form.addressKind !== 'phoneNumber') {
        errors['medium'] = 'A WhatsApp sender needs a phone number, not a messaging service.';
      }
      if (form.voiceEnabled) {
        need('apiKeySid', form.apiKeySid, 'The API key SID');
        need('apiKeySecret', form.apiKeySecret, 'The API key secret');
        if (form.addressKind !== 'phoneNumber') {
          errors['voiceEnabled'] = 'Voice needs a phone number, not a messaging service.';
        }
      }
      break;
    }
  }

  return errors;
}

// ------------------------------------------------------------ payload builders

const trimmedOrOmitted = (value: string) => {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const splitOrigins = (value: string): string[] =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const isBareOrigin = (value: string): boolean => {
  try {
    return new URL(value).origin === value;
  } catch {
    return false;
  }
};

/** The `channel` object for `POST /inboxes` — the server's validateConfig shape. */
export function toChannelBody(form: ChannelForm): Record<string, unknown> {
  switch (form.kind) {
    case 'oauth-email':
      return {
        type: 'email',
        provider: form.provider,
        ...(trimmedOrOmitted(form.email) ? { email: form.email.trim().toLowerCase() } : {}),
      };

    case 'manual-email':
      return {
        type: 'email',
        email: form.email.trim().toLowerCase(),
        imapEnabled: form.imapEnabled,
        ...(form.imapEnabled
          ? {
              imapAddress: form.imapAddress.trim(),
              imapPort: Number.parseInt(form.imapPort, 10) || 993,
              imapLogin: form.imapLogin.trim(),
              imapPassword: form.imapPassword,
              imapSsl: form.imapSsl,
            }
          : {}),
        smtpEnabled: form.smtpEnabled,
        ...(form.smtpEnabled
          ? {
              smtpAddress: form.smtpAddress.trim(),
              smtpPort: Number.parseInt(form.smtpPort, 10) || 587,
              smtpLogin: form.smtpLogin.trim(),
              smtpPassword: form.smtpPassword,
              smtpStarttls: form.smtpStarttls,
            }
          : {}),
      };

    case 'ringcentral':
      return {
        type: 'ringcentral',
        phoneNumber: form.phoneNumber.trim(),
        ...(form.auth === 'jwt'
          ? {
              clientId: form.clientId.trim(),
              clientSecret: form.clientSecret.trim(),
              jwt: form.jwt.trim(),
            }
          : {}),
      };

    case 'whatsapp':
      return {
        type: 'whatsapp',
        phoneNumber: form.phoneNumber.trim(),
        phoneNumberId: form.phoneNumberId.trim(),
        ...(trimmedOrOmitted(form.businessAccountId)
          ? { businessAccountId: form.businessAccountId.trim() }
          : {}),
        apiKey: form.apiKey.trim(),
        ...(trimmedOrOmitted(form.appSecret) ? { appSecret: form.appSecret.trim() } : {}),
      };

    case 'telegram':
      return { type: 'telegram', botToken: form.botToken.trim() };

    case 'facebook':
      return {
        type: 'facebook',
        pageId: form.pageId.trim(),
        pageAccessToken: form.pageAccessToken.trim(),
        ...(trimmedOrOmitted(form.appSecret) ? { appSecret: form.appSecret.trim() } : {}),
      };

    case 'instagram':
      return {
        type: 'instagram',
        accessToken: form.accessToken.trim(),
        ...(trimmedOrOmitted(form.appSecret) ? { appSecret: form.appSecret.trim() } : {}),
      };

    // Blank text fields are sent as '' on purpose: the adapter maps '' to NULL,
    // so clearing a welcome line in the manage form actually clears it.
    case 'website':
      return {
        type: 'website',
        widgetColor: form.widgetColor.trim(),
        widgetPosition: form.widgetPosition,
        welcomeTitle: form.welcomeTitle.trim(),
        welcomeTagline: form.welcomeTagline.trim(),
        launcherText: form.launcherText.trim(),
        allowedOrigins: splitOrigins(form.allowedOrigins),
        firebaseProjectId: form.firebaseProjectId.trim(),
      };

    case 'twilio':
      return {
        type: 'twilio',
        accountSid: form.accountSid.trim(),
        authToken: form.authToken.trim(),
        medium: form.medium,
        ...(form.addressKind === 'phoneNumber'
          ? { phoneNumber: form.phoneNumber.trim() }
          : { messagingServiceSid: form.messagingServiceSid.trim() }),
        voiceEnabled: form.voiceEnabled,
        ...(form.voiceEnabled
          ? { apiKeySid: form.apiKeySid.trim(), apiKeySecret: form.apiKeySecret.trim() }
          : {}),
      };
  }
}

export function toCreateBody(form: ChannelForm): { name: string; channel: Record<string, unknown> } {
  return { name: form.name.trim(), channel: toChannelBody(form) };
}

/**
 * The `channel` object for `PATCH /inboxes/:id`. Adapters merge field-by-field
 * and only overwrite secrets when truthy — blank secrets are dropped here so
 * "leave the stored password alone" is expressed by leaving the field empty.
 */
export function toChannelPatch(form: ChannelForm): Record<string, unknown> {
  const body = toChannelBody(form);
  delete body['type'];
  for (const key of SECRET_FIELDS) {
    if (body[key] === '' || body[key] === undefined) delete body[key];
  }
  return body;
}

// -------------------------------------------------- manage-screen form seeding

import type {
  EmailChannel,
  FacebookChannel,
  InstagramChannel,
  RingcentralChannel,
  TelegramChannel,
  TwilioChannel,
  WebsiteChannel,
  WhatsappChannel,
} from '../api/types';

/**
 * Structural view of a serialized inbox: the real `Inbox` union assigns to it,
 * and tests can pass partial channels.
 */
export type ManageInboxLike =
  | { name: string; channelType: 'email'; channel: Partial<EmailChannel> }
  | { name: string; channelType: 'ringcentral'; channel: Partial<RingcentralChannel> }
  | { name: string; channelType: 'telegram'; channel: Partial<TelegramChannel> }
  | { name: string; channelType: 'twilio'; channel: Partial<TwilioChannel> }
  | { name: string; channelType: 'website'; channel: Partial<WebsiteChannel> }
  | { name: string; channelType: 'whatsapp'; channel: Partial<WhatsappChannel> }
  | { name: string; channelType: 'facebook'; channel: Partial<FacebookChannel> }
  | { name: string; channelType: 'instagram'; channel: Partial<InstagramChannel> };

/**
 * Seed the re-credential form from a serialized inbox: non-secret fields
 * prefilled, secret fields blank (= keep stored). OAuth-backed email inboxes
 * have no credential form — they reconnect via consent — so this returns null
 * for them.
 */
export function formFromInbox(inbox: ManageInboxLike): ChannelForm | null {
  switch (inbox.channelType) {
    case 'email': {
      const c = inbox.channel;
      if (c.provider) return null;
      return {
        kind: 'manual-email',
        name: inbox.name,
        email: c.email ?? '',
        imapEnabled: !!c.imapEnabled,
        imapAddress: c.imapAddress ?? '',
        imapPort: c.imapPort != null ? String(c.imapPort) : '993',
        imapLogin: c.imapLogin ?? '',
        imapPassword: '',
        imapSsl: c.imapSsl !== false,
        smtpEnabled: !!c.smtpEnabled,
        smtpAddress: c.smtpAddress ?? '',
        smtpPort: c.smtpPort != null ? String(c.smtpPort) : '587',
        smtpLogin: c.smtpLogin ?? '',
        smtpPassword: '',
        smtpStarttls: c.smtpStarttls !== false,
      };
    }
    case 'ringcentral': {
      const c = inbox.channel;
      return {
        kind: 'ringcentral',
        name: inbox.name,
        phoneNumber: c.phoneNumber ?? '',
        auth: c.authType === 'jwt' ? 'jwt' : 'oauth',
        clientId: c.clientId ?? '',
        clientSecret: '',
        jwt: '',
      };
    }
    case 'whatsapp': {
      const c = inbox.channel;
      return {
        kind: 'whatsapp',
        name: inbox.name,
        phoneNumber: c.phoneNumber ?? '',
        phoneNumberId: c.phoneNumberId ?? '',
        businessAccountId: c.businessAccountId ?? '',
        apiKey: '',
        appSecret: '',
      };
    }
    case 'telegram':
      return { kind: 'telegram', name: inbox.name, botToken: '' };
    case 'facebook':
      return {
        kind: 'facebook',
        name: inbox.name,
        pageId: inbox.channel.pageId ?? '',
        pageAccessToken: '',
        appSecret: '',
      };
    case 'instagram':
      // An account riding on its Page has no credential of its own to update —
      // the Page channel's token is its token.
      if (inbox.channel.source === 'facebook_page') return null;
      return { kind: 'instagram', name: inbox.name, accessToken: '', appSecret: '' };
    case 'website': {
      const c = inbox.channel;
      return {
        kind: 'website',
        name: inbox.name,
        widgetColor: c.widgetColor ?? '#0E7C86',
        widgetPosition: c.widgetPosition === 'left' ? 'left' : 'right',
        welcomeTitle: c.welcomeTitle ?? '',
        welcomeTagline: c.welcomeTagline ?? '',
        launcherText: c.launcherText ?? '',
        allowedOrigins: (c.allowedOrigins ?? []).join('\n'),
        firebaseProjectId: c.firebaseProjectId ?? '',
      };
    }
    case 'twilio': {
      const c = inbox.channel;
      return {
        kind: 'twilio',
        name: inbox.name,
        accountSid: c.accountSid ?? '',
        authToken: '',
        medium: c.medium === 'whatsapp' ? 'whatsapp' : 'sms',
        addressKind: c.phoneNumber ? 'phoneNumber' : 'messagingServiceSid',
        phoneNumber: c.phoneNumber ?? '',
        messagingServiceSid: c.messagingServiceSid ?? '',
        voiceEnabled: !!c.voiceEnabled,
        apiKeySid: '',
        apiKeySecret: '',
      };
    }
  }
}
